import { Form, redirect, useActionData, useNavigation } from "react-router";
import type { Route } from "./+types/login";
import { getSession, createPendingMfaSession, createSession } from "~/domains/auth/session.server";
import { validateCredentials } from "~/domains/auth/password.server";
import { checkRateLimit, getClientKey, RATE_LIMITS } from "~/domains/auth/rate-limiter.server";
import { isMfaEnabled } from "~/domains/auth/totp.server";

// Redirect to dashboard if already logged in
export async function loader({ request }: Route.LoaderArgs) {
  const session = await getSession(request);
  if (session) {
    throw redirect("/dashboard");
  }
  return null;
}

export async function action({ request }: Route.ActionArgs) {
  // Rate limit login attempts
  const clientKey = getClientKey(request);
  const rateLimit = checkRateLimit(clientKey, RATE_LIMITS.login);
  if (!rateLimit.allowed) {
    return { error: "Too many login attempts. Please try again in a minute." };
  }

  const formData = await request.formData();
  const email = formData.get("email") as string;
  const password = formData.get("password") as string;

  if (!email || !password) {
    return { error: "Email and password are required" };
  }

  // Validate credentials against env vars
  const isValid = await validateCredentials(email, password);

  if (!isValid) {
    // Add a small delay to prevent timing attacks
    await new Promise((resolve) => setTimeout(resolve, 500));
    return { error: "Invalid email or password" };
  }

  // If MFA is disabled, create full session directly
  if (!isMfaEnabled()) {
    const sessionCookie = await createSession(email);
    throw redirect("/dashboard", {
      headers: {
        "Set-Cookie": sessionCookie,
      },
    });
  }

  // Create pending MFA session and redirect to MFA page
  const mfaCookie = await createPendingMfaSession(email);

  throw redirect("/login/mfa", {
    headers: {
      "Set-Cookie": mfaCookie,
    },
  });
}

export default function LoginPage() {
  const actionData = useActionData<typeof action>();
  const navigation = useNavigation();
  const isSubmitting = navigation.state === "submitting";

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold">Revenue Boost Admin</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Sign in to access the admin panel
          </p>
        </div>

        <Form method="post" className="space-y-4">
          {actionData?.error && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {actionData.error}
            </div>
          )}

          <div className="space-y-2">
            <label htmlFor="email" className="text-sm font-medium">
              Email
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="admin@example.com"
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="password" className="text-sm font-medium">
              Password
            </label>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="••••••••"
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
          >
            {isSubmitting ? "Signing in..." : "Sign in"}
          </button>
        </Form>
      </div>
    </div>
  );
}

